import { generatePath, useLocation, useNavigate, useParams } from 'react-router-dom';
import * as _ from 'lodash';
import { matchPath } from 'react-router-dom';
import * as path from 'path';

export type JourneyParams = {
  org_id: string;
  app_id?: string;
  user_id?: string;
};

export enum Routes {
  // apps
  ADMIN_HOME = `/admin/:org_id`,
  MANAGE_APPS = '/admin/:org_id/apps',
  CREATE_APP = '/admin/:org_id/apps/create',
  DELETE_APP = '/admin/:org_id/apps/delete/:app_id',
  APP_SOURCE_PROVIDER = '/admin/:org_id/apps/update-source-provider/:app_id',
  APP_SOURCE_MODE = '/admin/:org_id/apps/update-source-mode/:app_id',

  // settings
  SETTINGS = '/admin/:org_id/settings',

  // support
  SUPPORT = '/admin/:org_id/support',
  SUPPORT_CREATE = '/admin/:org_id/support/create-ticket',
  SUPPORT_TICKET = '/admin/:org_id/support/ticket/:ticket_id',

  BILLING = '/admin/:org_id/billing',
  BILLING_SETTINGS = '/admin/:org_id/billing/details',
  CARD_SETTINGS = '/admin/:org_id/billing/cards',

  // subscriptions
  MANAGE_PLANS = '/admin/:org_id/plans',
  UPGRADE_PLAN = '/admin/:org_id/plans/:plan_id/upgrade',
  PLAN_CARD_SETTINGS = '/admin/:org_id/plans/:plan_id/update-card',

  // users
  MANAGE_USERS = '/admin/:org_id/users',
  CREATE_DEV = '/admin/:org_id/users/create',
  EDIT_USER = '/admin/:org_id/users/edit/:user_id',
  INVITE_DEV = '/admin/:org_id/users/invites/create',
  EDIT_INVITE = '/admin/:org_id/users/invites/edit/:invite_id',

  // integrations
  MANAGE_INTEGRATIONS = '/admin/:org_id/integrations',
  CREATE_INTEGRATION = '/admin/:org_id/integrations/create',
  INSTALL_INTEGRATION = '/admin/:org_id/integrations/install/:integration_id',

  // roles
  MANAGE_ROLES = '/admin/:org_id/roles',

  // profile
  PROFILE_HOME = `/profile`,
  PROFILE_SECURITY = '/profile/security',
  SETUP_MFA = '/profile/security/configure-mfa',
  PROFILE_ORGANIZATIONS = '/profile/organizations',
  PROFILE_TOKENS = '/profile/tokens',
  PROFILE_GENERAL = '/profile/general'
}

export interface OrgParams {
  org_id: string;
}

export interface AppParams extends OrgParams {
  app_id: string;
}

export interface UserParams extends OrgParams {
  user_id?: string;
}

export interface PlanParams extends OrgParams {
  plan_id?: string;
}

export interface IntegrationParams extends OrgParams {
  integration_id: string;
}

export interface SupportTicketParams extends OrgParams {
  ticket_id: string;
}

export interface RouteParams {
  [key: string]: any;
  [Routes.ADMIN_HOME]: OrgParams;
  [Routes.MANAGE_APPS]: OrgParams;
  [Routes.MANAGE_INTEGRATIONS]: OrgParams;
  [Routes.CREATE_APP]: OrgParams;
  [Routes.APP_SOURCE_PROVIDER]: AppParams;
  [Routes.APP_SOURCE_MODE]: AppParams;
  [Routes.MANAGE_USERS]: OrgParams;
  [Routes.EDIT_USER]: UserParams;
  [Routes.INSTALL_INTEGRATION]: IntegrationParams;
  [Routes.MANAGE_ROLES]: OrgParams;
  [Routes.UPGRADE_PLAN]: PlanParams;
  [Routes.PLAN_CARD_SETTINGS]: PlanParams;
  [Routes.SETTINGS]: OrgParams;
  [Routes.BILLING_SETTINGS]: OrgParams;
  [Routes.CARD_SETTINGS]: OrgParams;
  [Routes.SUPPORT]: OrgParams;
  [Routes.SUPPORT_CREATE]: OrgParams;
  [Routes.SUPPORT_TICKET]: SupportTicketParams;
}

export function useMatchedRoute(): Routes | null {
  const { pathname } = useLocation();
  for (const route of _.values(Routes)) {
    if (matchPath(route, pathname)) {
      return route;
    }
  }
  return null;
}

export interface JourneyNavigationHandler<T extends Routes = Routes.ADMIN_HOME> {
  params: RouteParams[T];
  matched: Routes | null;
  relativeRoute: (rel: Routes) => string;
  back: () => any;
  navigate: <T extends Routes>(route: T, params: RouteParams[T]) => any;
}

export const useJourneyNavigation = <T extends Routes = Routes.ADMIN_HOME>(r: T): JourneyNavigationHandler<T> => {
  const navigate = useNavigate();
  const params = useParams() as RouteParams[typeof r];
  const matched = useMatchedRoute();
  return {
    params,
    matched,
    relativeRoute: (rel: Routes) => {
      return path.join(path.relative(r, rel), '*');
    },
    back: () => {
      navigate(-1 as any, {
        replace: true
      });
    },
    navigate: <T extends Routes>(route: T, params: RouteParams[T]) => {
      navigate(generatePath(route, params), {
        replace: true
      });
    }
  };
};

export function resolveRouteItems(items: SidebarItem[], params: JourneyParams | null | undefined) {
  if (!params) {
    return items;
  }
  return items.map((item) => {
    const path = generatePath(item.path, params);
    return { ...item, path: path };
  });
}

export interface SidebarItem {
  key: string;
  label: string;
  icon?: string | JSX.Element;
  path: string;
  disabled?: boolean;
}
